import {
  WorkgroupCurrencyPatch,
  WorkgroupCurrencyUpdateTypeInput,
} from 'common/types/globalTypes';
import { intersection } from 'lodash';
import { CorporateWorkgroup } from './__generated__/CorporateWorkgroup';
import {
  CORPORATE_WORKGROUP_VALUES,
  WORKGROUP_USERS_VALUES
} from './constants';
import {
  CorporateWorkgroupValues,
  workgroupCurrencyType,
  workgroupUserType,
} from './types';

interface DefaultValueProps {
  isNew: boolean;
  corporateWorkgroupData: CorporateWorkgroup | undefined;
}

export const getDefaultValues = (props: DefaultValueProps) => {
  const { isNew, corporateWorkgroupData } = { ...props };
  let defaultValues: CorporateWorkgroupValues = CORPORATE_WORKGROUP_VALUES;
  if (corporateWorkgroupData?.corporateWorkgroup && !isNew) {
    const {
      id,
      _rowTimestamp,
      _isUpdatable,
      _isDeletable,
      corporateChartOfAccount,
      statusType,
      workgroupUsersByWorkgroupId,
      workgroupCurrenciesByWorkgroupId,
      ...workgroupValues
    } = { ...corporateWorkgroupData.corporateWorkgroup };
    const { nodes } = {
      ...corporateWorkgroupData?.corporateWorkgroup
        ?.workgroupUsersByWorkgroupId,
    };
    const { nodes: currencyNodes } = {
      ...corporateWorkgroupData?.corporateWorkgroup
        ?.workgroupCurrenciesByWorkgroupId,
    };
    defaultValues = {
      ...workgroupValues,
      chartOfAccountsId: corporateChartOfAccount?.id!,
      workgroupUsers: !!nodes?.length
        ? [
            ...nodes?.map((node) => ({
              name: node?.userProfile?.name,
              emailAccount: node?.userProfile?.emailAccount,
              profileIconState:
                node?.userProfile?._signinInfo?.profileIconState,
              profileIconType: node?.userProfile?._signinInfo?.profileIconType,
              _lastUsedDate: node?.userProfile?._lastUsedDate,
              id: node.id,
              _rowTimestamp: node._rowTimestamp,
              userProfileId: node.userProfileId,
              _isUpdatable: node._isUpdatable,
              _isDeletable: node._isDeletable,
            })),
            WORKGROUP_USERS_VALUES,
          ]
        : [WORKGROUP_USERS_VALUES],

      workgroupCurrencies: !!currencyNodes?.length
        ? [
            ...currencyNodes?.map((node) => ({
              id: node.id,
              _rowTimestamp: node._rowTimestamp,
              name: node.name,
              currencyId: node.currencyId,
              exchangeRate: node.exchangeRate,
              spotRate: node.spotRate,
              isSpotRatePreferred: node.isSpotRatePreferred,
              _isActive: node._isActive,
              _isDeletable: node._isDeletable,
              _isUpdatable: node._isUpdatable,
            })),
          ]
        : [],
    };
  }
  return defaultValues;
};

export const getCreatedWorkgroupUsers = (
  defaultValues: workgroupUserType[],
  currentValues: workgroupUserType[]
) => {
  const defaultUserProfileIds = new Set(
    defaultValues.map((item) => item.userProfileId)
  );
  const newUsers = currentValues
    .filter(
      (item) =>
        item.userProfileId && !defaultUserProfileIds.has(item.userProfileId)
    )
    .map((item) => ({
      userProfileId: item.userProfileId!,
    }));
  return newUsers;
};

export const getDeletedWorkgroupUsers = (
  defaultValues: workgroupUserType[],
  currentValues: workgroupUserType[]
) => {
  const currentUserIds = new Set(
    currentValues.map((item) => item.userProfileId)
  );
  const deletedUsers = defaultValues
    .filter((item) => item.id && !currentUserIds.has(item.userProfileId))
    .map((item) => ({
      id: item.id!,
      rowTimestamp: item._rowTimestamp!,
    }));

  return deletedUsers;
};

export const getCurrencypatch = (
  defaultObjs: workgroupCurrencyType[],
  formValueObjs: workgroupCurrencyType[]
): WorkgroupCurrencyUpdateTypeInput[] => {
  return intersection(
    defaultObjs.map((curr) => curr.id),
    formValueObjs.filter((curr) => curr.id).map((curr) => curr.id)
  ).reduce((arr, targetId) => {
    const initialValidCurrency = defaultObjs.find(
      (curr) => curr.id === targetId
    )!;
    const { id, _rowTimestamp, ...updatedCurrencies } = formValueObjs.find(
      (curr) => curr.id === targetId
    )!;

    const patch = Object.entries(updatedCurrencies).reduce(
      (res, [key, val]) => {
        if (val !== initialValidCurrency[key as keyof workgroupCurrencyType]) {
          return { ...res, [key]: val };
        }
        return res;
      },
      {}
    );

    if (Object.keys(patch).length > 0) {
      arr.push({
        id,
        rowTimestamp: _rowTimestamp,
        workgroupCurrencyPatch: patch as WorkgroupCurrencyPatch,
      });
    }
    return arr;
  }, [] as WorkgroupCurrencyUpdateTypeInput[]);
};
